import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clinicApi } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/usePermissions';
import type { PatientSex } from '@/types/patients';

export type AptTicketStatus = 'waiting' | 'cancelled' | 'recorded';

/** A queue number as the issue/cancel endpoints return it. */
export interface AptTicket {
  id: string;
  queueDate: string;
  number: number;
  status: AptTicketStatus;
  patientId: string;
  createdAt: string;
}

/** A queue row: the number plus the demographics of whoever holds it. */
export interface AptTicketListItem {
  id: string;
  queueDate: string;
  number: number;
  status: AptTicketStatus;
  /** The visit this number became; null unless `status` is `recorded`. */
  consultationId: string | null;
  createdAt: string;
  patient: {
    id: string;
    mrn: string;
    fullName: string;
    sex: PatientSex;
    dateOfBirth: string | null;
  };
}

export interface AptTicketListing {
  queueDate: string;
  tickets: AptTicketListItem[];
}

export const APT_TICKETS_KEY = ['apt-tickets'] as const;

/**
 * Today's queue. "Today" is the server's, in the clinic's timezone — the
 * request names no date, so a tablet with a wrong clock still sees the right
 * day.
 *
 * Disabled for a member without `apt_counter.read` rather than left to 403:
 * this is called from the registry's patient pane, which a member can reach
 * without holding the counter key at all.
 */
export function useAptTickets(opts: { refetchInterval?: number } = {}) {
  const { isAuthenticated, activeClinic } = useAuth();
  const canRead = useHasPermission('apt_counter.read');
  return useQuery({
    queryKey: [...APT_TICKETS_KEY, activeClinic?.id ?? null],
    queryFn: () => clinicApi.get<AptTicketListing>('apt-counter/tickets'),
    enabled: isAuthenticated && !!activeClinic && canRead,
    staleTime: 5_000,
    refetchInterval: opts.refetchInterval,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: APT_TICKETS_KEY });
}

export function useIssueTicket() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (patientId: string) =>
      clinicApi.post<AptTicket>('apt-counter/tickets', { patientId }),
    // Settled, not success: a 409 means the patient already holds a number
    // this screen did not know about, and the refetch is what shows it.
    onSettled: invalidate,
  });
}

export function useCancelTicket() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (ticketId: string) =>
      clinicApi.post<AptTicket>(`apt-counter/tickets/${ticketId}/cancel`),
    onSettled: invalidate,
  });
}
